import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getNavSectionsByRole } from '../../config/navigation';

function NavIcon({ icon: Icon }) {
  return <Icon size={18} />;
}

const PORTAL_LABELS = {
  superadmin:  'Admin Portal',
  owner:       'Landlord Portal',
  manager:     'Manager Portal',
  tenant:      'Tenant Portal',
  maintenance: 'Staff Portal',
};

function UserFooter() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();

  // Build initials from name, falling back to email
  const profile = user?.profile || {};
  const firstName = profile.first_name || user?.first_name || '';
  const lastName  = profile.last_name  || user?.last_name  || '';

  const initials = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : firstName
      ? firstName.slice(0,2).toUpperCase()
      : (user?.email || 'U').slice(0,2).toUpperCase();

  const displayName = firstName
    ? `${firstName} ${lastName}`.trim()
    : user?.email || 'User';

  const roleLabel = (user?.role || '').replace(/_/g, ' ');

  return (
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer group"
           onClick={() => navigate('/settings')}>
        <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
          <div className="text-xs text-gray-400 capitalize">{roleLabel}</div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); logout(); }}
          title="Logout"
          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const sections  = getNavSectionsByRole(user?.role || '');
  const portalLabel = PORTAL_LABELS[user?.role] || 'Admin Portal';

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-white border-r border-gray-200 hidden lg:flex flex-col">
      {/* Logo */}
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="LPMS" className="w-10 h-10 object-contain"
            onError={e => { e.target.style.display='none'; }} />
          <div>
            <div className="font-bold text-gray-900 text-lg leading-tight">LPMS</div>
            <div className="text-xs text-gray-400">{portalLabel}</div>
          </div>
        </div>
      </div>

      {/* Nav sections */}
      <div className="flex-1 overflow-y-auto py-4">
        {Object.entries(sections).map(([sectionName, items]) => (
          <div key={sectionName} className="mb-5">
            <div className="px-4 mb-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {sectionName}
            </div>
            {items.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                <NavIcon icon={item.icon} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <UserFooter />
    </aside>
  );
}
